import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIcon } from '@angular/material/icon';
import { finalize } from 'rxjs';
import {
  REQUEST_EMAIL_UI_CONFIG,
  RequestEmailProblemDetails,
  RequestEmailResponse
} from '../models/request-email.models';
import { Request } from '../services/request.service';
import { RequestEmailService } from '../services/request-email.service';

@Component({
  selector: 'app-request-email-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIcon],
  templateUrl: './request-email-modal.component.html',
  styleUrl: './request-email-modal.component.css'
})
export class RequestEmailModalComponent {
  @Input({ required: true }) request!: Request;
  @Input({ required: true }) senderEmail!: string;
  @Output() closed = new EventEmitter<void>();
  @Output() sent = new EventEmitter<RequestEmailResponse>();

  readonly limits = REQUEST_EMAIL_UI_CONFIG;

  subjectSuffix = '';
  body = '';
  ccInput = '';
  ccAddresses: string[] = [];
  files: File[] = [];
  error: string | null = null;
  sending = false;

  constructor(private readonly requestEmailService: RequestEmailService) {}

  get recipient(): string {
    return `${this.limits.recipientPrefix}${this.request.projectNumber}@${this.limits.recipientDomain}`;
  }

  get subjectPrefix(): string {
    return `${this.request.projectNumber} ${this.request.requestType.toUpperCase()} ${this.request.number}`;
  }

  get combinedFileSize(): number {
    return this.files.reduce((total, file) => total + file.size, 0);
  }

  addCcAddresses(): boolean {
    const candidates = this.ccInput
      .split(/[,;\n]+/)
      .map(address => address.trim())
      .filter(Boolean);

    if (candidates.length === 0) {
      this.ccInput = '';
      return true;
    }

    const invalidAddress = candidates.find(address => !this.isValidEmail(address));
    if (invalidAddress) {
      this.error = `"${invalidAddress}" is not a valid CC email address.`;
      return false;
    }

    const existing = new Set(this.ccAddresses.map(address => address.toLowerCase()));
    candidates.forEach(address => {
      const normalized = address.toLowerCase();
      if (!existing.has(normalized)) {
        this.ccAddresses.push(address);
        existing.add(normalized);
      }
    });

    this.ccInput = '';
    this.error = null;
    return true;
  }

  onCcKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ',' && event.key !== ';') return;

    event.preventDefault();
    this.addCcAddresses();
  }

  removeCcAddress(index: number): void {
    this.ccAddresses.splice(index, 1);
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files ?? []);
    input.value = '';

    if (selectedFiles.length === 0) return;

    const mergedFiles = this.mergeFiles(this.files, selectedFiles);
    const validationError = this.validateFiles(mergedFiles);
    if (validationError) {
      this.error = validationError;
      return;
    }

    this.files = mergedFiles;
    this.error = null;
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.error = null;
  }

  send(): void {
    if (this.sending || !this.addCcAddresses()) return;

    const validationError = this.validateForm();
    if (validationError) {
      this.error = validationError;
      return;
    }

    this.sending = true;
    this.error = null;

    this.requestEmailService.send({
      projectNumber: this.request.projectNumber,
      requestType: this.request.requestType,
      requestNumber: this.request.number,
      subjectSuffix: this.subjectSuffix,
      body: this.body,
      cc: [...this.ccAddresses],
      files: [...this.files]
    })
      .pipe(finalize(() => {
        this.sending = false;
      }))
      .subscribe({
        next: response => this.sent.emit(response),
        error: error => {
          this.error = this.getErrorMessage(error);
        }
      });
  }

  close(): void {
    if (!this.sending) {
      this.closed.emit();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  private validateForm(): string | null {
    if (!this.body.trim()) {
      return 'Enter an email message before sending.';
    }

    if (/[\r\n\u0000]/.test(this.subjectSuffix)) {
      return 'The subject addition cannot contain line breaks or control characters.';
    }

    return this.validateFiles(this.files);
  }

  private validateFiles(files: File[]): string | null {
    if (files.length > this.limits.maxFiles) {
      return `A maximum of ${this.limits.maxFiles} files can be attached.`;
    }

    const oversizedFile = files.find(file => file.size > this.limits.maxFileBytes);
    if (oversizedFile) {
      return `${oversizedFile.name} exceeds the ${this.formatBytes(this.limits.maxFileBytes)} per-file limit.`;
    }

    const totalBytes = files.reduce((total, file) => total + file.size, 0);
    if (totalBytes > this.limits.maxCombinedBytes) {
      return `The combined attachment size exceeds ${this.formatBytes(this.limits.maxCombinedBytes)}.`;
    }

    return null;
  }

  private mergeFiles(existing: File[], selected: File[]): File[] {
    const files = [...existing];
    const keys = new Set(
      existing.map(file => `${file.name}:${file.size}:${file.lastModified}`)
    );

    selected.forEach(file => {
      const key = `${file.name}:${file.size}:${file.lastModified}`;
      if (!keys.has(key)) {
        files.push(file);
        keys.add(key);
      }
    });

    return files;
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    const problem = error.error as RequestEmailProblemDetails | null;
    if (problem?.message) return problem.message;
    if (problem?.detail) return problem.detail;

    if (error.status === 413) {
      return 'The attachments exceed the server upload limit.';
    }
    if (error.status === 429) {
      return 'The email send limit has been reached. Please wait before trying again.';
    }
    if (error.status === 502) {
      return 'The email provider could not send this message. Please try again.';
    }

    return 'The request email could not be sent. Please try again.';
  }
}
